import { Component, computed, effect, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { LiveChartArrowModel } from '../../models/live-chart-arrow-model';
import { LiveChartHelper } from './live-chart-helper';
import { catchError, from, interval, of } from 'rxjs';
import { TotalEnergyResponse } from '../../../../api/models/total-energy-response';
import { DecimalPipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { environment } from '../../../../environments/environment';
import { Api } from '../../../../api/api';
import { getTotalEnergyReading } from '../../../../api/functions';
import { LocationStateService } from '../../../../common/services/location-state.service';
import { RouterLink } from '@angular/router';

registerLocaleData(localeDe);

@Component({
  selector: 'app-live-chart',
  imports: [RouterLink],
  templateUrl: './live-chart.component.html',
  styleUrls: ['./live-chart.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DecimalPipe],
})
export class LiveChartComponent {
  private decimalPipe = inject(DecimalPipe);

  readonly environment = environment;
  api = inject(Api);
  locationStateService = inject(LocationStateService);

  params = computed(() => ({
    location_id: this.locationStateService.currentLocation()?.id ?? '',
  }));
  arrowModel: WritableSignal<LiveChartArrowModel> = signal<LiveChartArrowModel>({});
  poll = toSignal(interval(10000));
  resourceValue = computed(() => this.resource.value());
  arrowModelValue = computed(() => this.arrowModel());

  private readonly SAMPLE: TotalEnergyResponse = {
    production: -11.80214,
    consumption: -0.24686,
    grid: 12.2,
    batteryStatusPercent: 100,
    batteryStatusKwh: 100,
    batteryCalculatedStatusKwh: 120,
    batteryCalculatedStatusPercent: 1000,
  };
  resource = rxResource<TotalEnergyResponse, { location_id: string }>({
    params: this.params,
    defaultValue: environment.showDefault ? this.SAMPLE : undefined,
    stream: (loaderParams) => {
      return from(this.api.invoke(getTotalEnergyReading, loaderParams.params)).pipe(
        catchError((error) => {
          console.error('Service is not reachable. Using default values.', error);
          // Using default values instead of random samples
          return of({
            production: -11.80214,
            consumption: -0.24686,
            grid: 12.2,
            batteryStatusPercent: 100,
            batteryStatusKwh: 100,
            batteryCalculatedStatusKwh: 120,
            batteryCalculatedStatusPercent: 1000,
          } as TotalEnergyResponse);
          // Original random sample code:
          // return of(SAMPLES[Math.floor(Math.random() * SAMPLES.length)] as TotalEnergyResponse);
        }),
      );
    },
  });

  constructor() {
    effect(() => {
      this.poll();
      this.resource.reload();
    });
    effect(() => {
      const value = this.resourceValue();
      if (value) {
        this.updateChartData(value);
        this.calculateArrowProperties(value);
        this.changeWidthOfLine(value);
      }
    });
    effect(() => {
      // Recalculate line widths when the selected location changes,
      // so the performance chart maximum is applied immediately.
      this.locationStateService.currentLocation();
      const value = this.resourceValue();
      if (value) {
        this.changeWidthOfLine(value);
      }
    });
  }

  private updateChartData(data: TotalEnergyResponse) {
    if (!data) return;

    // Update net value
    const netValue = document.getElementById('net-value');
    const netKw = document.getElementById('net-kw');
    if (netValue && netKw && data.grid) {
      const formatted = this.formatNumber(data.grid);
      netValue.innerHTML = formatted;
      switch (formatted.length) {
        case 1:
          //for <10 values, align elements differently
          netValue.setAttribute('x', '270');
          netKw.setAttribute('x', '285');
          break;
        case 3:
          //for >100 values, align elements differently
          netValue.setAttribute('x', '260');
          netKw.setAttribute('x', '292');
          break;
        case 4:
          //for >1000 values, align elements differently
          netValue.setAttribute('x', '260');
          netKw.setAttribute('x', '296');
          break;
        case 5:
          //for >20,20 values, align elements differently
          netValue.setAttribute('x', '253');
          netKw.setAttribute('x', '298');
          break;
        case 6:
          netValue.setAttribute('x', '250');
          netKw.setAttribute('x', '305');
          break;
        case 7:
          //for -120,20 values, align elements differently
          netValue.setAttribute('x', '245');
          netKw.setAttribute('x', '305');
          break;
        case 8:
          //for -1220,20 values, align elements differently
          netValue.setAttribute('x', '240');
          netKw.setAttribute('x', '308');
          break;
      }
    }

    // Update battery value and SOC
    const batteryValue = document.getElementById('battery-value');
    const batteryKW = document.getElementById('battery-value-kw');
    if (batteryValue && batteryKW && data.batteryStatusKwh !== undefined) {
      const formatted = this.formatNumber(data.batteryStatusKwh);
      batteryValue.innerHTML = formatted;
      switch (formatted.length) {
        case 1:
          //for <10 values, align elements differently
          batteryValue.setAttribute('x', '130');
          batteryKW.setAttribute('x', '145');
          break;
        case 3:
          //for >100 values, align elements differently
          batteryValue.setAttribute('x', '121');
          batteryKW.setAttribute('x', '152');
          break;
        case 4:
          //for >1000 values, align elements differently
          batteryValue.setAttribute('x', '120');
          batteryKW.setAttribute('x', '155');
          break;
        case 5:
          //for >1000 values, align elements differently
          batteryValue.setAttribute('x', '115');
          batteryKW.setAttribute('x', '160');
          break;
        case 6:
          //for -11,45 values, align elements differently
          batteryValue.setAttribute('x', '110');
          batteryKW.setAttribute('x', '164');
          break;
        case 7:
          //for -11,45 values, align elements differently
          batteryValue.setAttribute('x', '105');
          batteryKW.setAttribute('x', '165');
          break;
        case 8:
          //for -1220,20 values, align elements differently
          batteryValue.setAttribute('x', '102');
          batteryKW.setAttribute('x', '170');
          break;
      }
    }
    const batterySocElem = document.getElementById('battery-soc-val');
    if (batterySocElem) {
      batterySocElem.innerHTML = data.batteryStatusPercent.toString();
    }
    this.adjustSOC(data.batteryStatusPercent);

    // update production value
    const productionValue = document.getElementById('production-value');
    const productionKW = document.getElementById('production-kw');
    if (productionValue && productionKW && data.production !== undefined) {
      const formatted = this.formatNumber(data.production * -1); //show production in live chart always positive (coming negativ from server)
      productionValue.innerHTML = formatted;
      switch (formatted.length) {
        case 1:
          //for <10 values, align elements differently
          productionValue.setAttribute('x', '170');
          productionKW.setAttribute('x', '185');
          break;
        case 3:
          //for >100 values, align elements differently
          productionValue.setAttribute('x', '165');
          productionKW.setAttribute('x', '195');
          break;
        case 4:
          //for >1000 values, align elements differently
          productionValue.setAttribute('x', '157');
          productionKW.setAttribute('x', '200');
          break;
        case 5:
          //for 10,20 values, align elements differently
          productionValue.setAttribute('x', '155');
          productionKW.setAttribute('x', '200');
          break;
        case 6:
          //for -10,20 values, align elements differently
          productionValue.setAttribute('x', '150');
          productionKW.setAttribute('x', '203');
          break;
        case 7:
          //for -10,20 values, align elements differently
          productionValue.setAttribute('x', '145');
          productionKW.setAttribute('x', '205');
          break;
        case 8:
          //for -10,20 values, align elements differently
          productionValue.setAttribute('x', '140');
          productionKW.setAttribute('x', '212');
          break;
      }
    }

    // update consumption value
    const consumptionValue = document.getElementById('consumption-value');
    const consumptionKW = document.getElementById('consumption-kw');
    if (consumptionValue && consumptionKW && data.consumption !== undefined) {
      const formatted = this.formatNumber(data.consumption);
      consumptionValue.innerHTML = formatted;
      switch (formatted.length) {
        case 1:
          //for <10 values, align elements differently
          consumptionValue.setAttribute('x', '35');
          consumptionKW.setAttribute('x', '50');
          break;
        case 3:
          //for >100 values, align elements differently
          consumptionValue.setAttribute('x', '28');
          consumptionKW.setAttribute('x', '60');
          break;
        case 4:
          //for >1000 values, align elements differently
          consumptionValue.setAttribute('x', '25');
          consumptionKW.setAttribute('x', '62');
          break;
        case 5:
          //for >1000 values, align elements differently
          consumptionValue.setAttribute('x', '20');
          consumptionKW.setAttribute('x', '65');
          break;
        case 6:
          //for >1000 values, align elements differently
          consumptionValue.setAttribute('x', '15');
          consumptionKW.setAttribute('x', '68');
          break;
        case 7:
          //for >1000 values, align elements differently
          consumptionValue.setAttribute('x', '10');
          consumptionKW.setAttribute('x', '72');
          break;
        case 8:
          //for >1000 values, align elements differently
          consumptionValue.setAttribute('x', '5');
          consumptionKW.setAttribute('x', '77');
          break;
      }
    }
  }

  private adjustSOC(socVal: number) {
    const socPath = document.getElementsByClassName('battery-soc-light-green')[0] as HTMLElement;
    const socText = document.getElementById('battery-soc-val') as HTMLElement;
    const socPercent = document.getElementById('battery-soc-percent') as HTMLElement;
    const inputVal = socVal * 0.87; // "translates" the input value to fit the SVG path
    // adjust text positions based on soc value
    if (socVal < 10) {
      socPercent.setAttribute('x', '145');
      socText.setAttribute('x', '135');
    } else if (socVal < 100) {
      socPercent.setAttribute('x', '150');
      socText.setAttribute('x', '133');
    } else if (socVal === 100) {
      socPercent.setAttribute('x', '156');
      socText.setAttribute('x', '130');
    }
    // remove previous soc path if needed
    if (socVal === 0 && socPath) {
      socPath.remove();
      return;
    }
    if (socVal > 0 && socPath) {
      socPath.remove();
    }
    let attr = '';
    if (socVal === 100 || socVal === 99) {
      attr = 'M104.69,152h78.51c3.31,0,5.99,1.79,5.99,4v56c0,2.21-2.68,4-5.99,4h-78.51c-3.3,0-5.99-1.8-5.99-4v-56c0-2.2,2.7-4,5.99-4Z';
    } else if (socVal === 97 || socVal === 98) {
      attr = 'M104.61,152h77.38c3.26,0,5.91,1.79,5.91,4v56c0,2.21-2.64,4-5.91,4h-77.38c-3.25,0-5.91-1.8-5.91-4v-56c0-2.2,2.66-4,5.91-4Z';
    }

    const svgElement = document.getElementById('live-chart') as HTMLElement;
    if (svgElement) {
      const newPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      newPath.setAttribute('class', 'st8 battery-soc-light-green');
      if (attr) {
        // for 100% - rounded corners needed there!
        newPath.setAttribute('d', attr);
      } else {
        // for the normal path, without rounded corners for the right side of the rectangle
        newPath.setAttribute('d', 'M102.7,152.2 h' + inputVal + ' v64 h-' + inputVal + ' c-2.2,0-4-1.8-4-4 v-56 c0-2.2,1.8-4,4-4Z');
      }
      const referenceElement = svgElement.querySelector('.batterylink');
      //decide if the element already exists or add if not (if value was 0 before -> element was removed)
      if (referenceElement) {
        referenceElement.prepend(newPath);
      } else {
        svgElement.appendChild(newPath);
      }
    }
  }

  private calculateArrowProperties(data: TotalEnergyResponse) {
    this.arrowModel.set(LiveChartHelper.calculateArrowProperties(data));
  }

  private setLineStroke(mainSelector: string, backgroundSelector: string, strokeWidth: number) {
    if (strokeWidth > 25) strokeWidth = 25;
    const mainLine = document.getElementsByClassName(mainSelector)[0] as HTMLElement;
    const bgLine = document.getElementsByClassName(backgroundSelector)[0] as HTMLElement;
    if (mainLine && bgLine) {
      mainLine.style.strokeWidth = strokeWidth + 'px';
      bgLine.style.strokeWidth = strokeWidth + 'px';
    }
  }

  private changeWidthOfLine(data: TotalEnergyResponse) {
    const arrow = this.arrowModel();
    const maxValueOfSite =
      this.locationStateService.currentLocation()?.displayChart?.performance?.maxkWh ?? 100;
    // Line 1: Production to Consumption arrow.production_to_consumption
    if (arrow.production_to_consumption) {
      this.setLineStroke(
        'line-production-consumption-yellow',
        'line-production-consumption-yellow-background',
        this.calculateWidthOfLine(maxValueOfSite, arrow.production_to_consumption),
      );
    } else {
      this.setLineStroke('line-production-consumption-yellow', 'line-production-consumption-yellow-background', 0);
    }

    // Line 2: Production to Battery
    if (arrow.production_to_battery) {
      this.setLineStroke(
        'line-production-battery-yellow',
        'line-production-battery-yellow-background',
        this.calculateWidthOfLine(maxValueOfSite, arrow.production_to_battery),
      );
    } else {
      this.setLineStroke('line-production-battery-yellow', 'line-production-battery-yellow-background', 0);
    }

    // Line 3: Production to Grid
    if (arrow.production_to_grid) {
      this.setLineStroke(
        'line-production-net-yellow',
        'line-production-net-yellow-background',
        this.calculateWidthOfLine(maxValueOfSite, arrow.production_to_grid),
      );
    } else {
      this.setLineStroke('line-production-net-yellow', 'line-production-net-yellow-background', 0);
    }

    // Line 4: Consumption to Battery vs. Battery to Consumption
    if (arrow.consumption_to_battery) {
      this.setLineStroke(
        'line-consumption-battery-blue',
        'line-consumption-battery-blue-background',
        this.calculateWidthOfLine(maxValueOfSite, arrow.consumption_to_battery),
      );
      this.setLineStroke('line-battery-consumption-green', 'line-battery-consumption-green-background', 0);
    } else if (arrow.battery_to_consumption) {
      this.setLineStroke(
        'line-battery-consumption-green',
        'line-battery-consumption-green-background',
        this.calculateWidthOfLine(maxValueOfSite, arrow.battery_to_consumption),
      );
      this.setLineStroke('line-consumption-battery-blue', 'line-consumption-battery-blue-background', 0);
    } else {
      this.setLineStroke('line-consumption-battery-blue', 'line-consumption-battery-blue-background', 0);
      this.setLineStroke('line-battery-consumption-green', 'line-battery-consumption-green-background', 0);
    }

    // Line 5: Battery to Grid vs. Grid to Battery
    if (arrow.grid_to_battery) {
      this.setLineStroke('line-net-battery-black', 'line-net-battery-black-background', this.calculateWidthOfLine(maxValueOfSite, arrow.grid_to_battery));
      this.setLineStroke('line-battery-net-green', 'line-battery-net-green-background', 0);
    } else if (arrow.battery_to_grid) {
      this.setLineStroke('line-battery-net-green', 'line-battery-net-green-background', this.calculateWidthOfLine(maxValueOfSite, arrow.battery_to_grid));
      this.setLineStroke('line-net-battery-black', 'line-net-battery-black-background', 0);
    } else {
      this.setLineStroke('line-net-battery-black', 'line-net-battery-black-background', 0);
      this.setLineStroke('line-battery-net-green', 'line-battery-net-green-background', 0);
    }

    // Line 6: Consumption to Grid vs. Grid to Consumption
    if (arrow.consumption_to_grid) {
      //-6
      this.setLineStroke(
        'line-consumption-grid-blue',
        'line-consumption-grid-blue-background',
        this.calculateWidthOfLine(maxValueOfSite, arrow.consumption_to_grid),
      );
      this.setLineStroke('line-grid-consumption-black', 'line-grid-consumption-black-background', 0);
    } else if (arrow.grid_to_consumption) {
      //6
      this.setLineStroke(
        'line-grid-consumption-black',
        'line-grid-consumption-black-background',
        this.calculateWidthOfLine(maxValueOfSite, arrow.grid_to_consumption),
      );
      this.setLineStroke('line-consumption-grid-blue', 'line-consumption-grid-blue-background', 0);
    } else {
      this.setLineStroke('line-consumption-grid-blue', 'line-consumption-grid-blue-background', 0);
      this.setLineStroke('line-grid-consumption-black', 'line-grid-consumption-black-background', 0);
    }
  }

  private formatNumber(value: number): string {
    return this.decimalPipe.transform(value, '1.2-2', 'de-DE')!;
  }

  private calculateWidthOfLine(maxValueOfSite: number, val: number) {
    const maxLineWidth = 25;
    // min arrow power is configured per location; fall back to 1% of max site value (SCRUM-199)
    const minValueForLine =
      this.locationStateService.currentLocation()?.displayChart?.liveChartMinArrowPowerkW ?? maxValueOfSite * 0.01;

    if (val < minValueForLine) {
      //don't show line if value is below the site's min arrow power
      return 0;
    }
    const calcValue = val / maxValueOfSite;
    return maxLineWidth * calcValue;
  }
}
